'use client';

import { useEffect, useMemo } from 'react';
import { useGetCurrentPageMaverick } from '@/hooks/getCurrentPage';
import { usePathname } from 'next/navigation';
import { Trans, useTranslation } from '@whoop/i18n/lang/client';
import { FrequentlyAskedQuestion } from 'ui/components/FAQ/FAQ';
import {
  APP_WHOOP_GIFT_EXTENSIONS_URL,
  TERMS_OF_USE_URL,
} from 'ui/utils/urlUtils';
import { env } from 'env';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  CustomAccordionTrigger,
} from 'ui/components/AccordionV3/Accordion';
import { Separator } from 'ui/components/Separator/Separator';
import { CaretDown } from 'ui/icons/icons/Navigation/CaretDown';

import { useSiteStore } from '@/store/SiteStoreProvider';
import { useShallow } from 'zustand/react/shallow';
import { getMembershipByAttributes } from '@/lib/utils/maverickHelpers';
import { MembershipTier } from '@/lib/constants/maverick';
import { formatDisplayPrice } from '@whoop/i18n/utils/priceHelpers';
import { getPriceValues } from 'ui/utils/variantHelpers';
import { buildFaqs } from './faqHelpers';
import { Language } from '@whoop/i18n/types/internationalization';
import { Product } from 'services/generated/commerce-service/models/Product';
import { MembershipCatalog } from 'services/generated/commerce-service/models/MembershipCatalog';
import { JoinCatalogResourceV2Service } from 'services/generated/commerce-service/services/JoinCatalogResourceV2Service';
import { OpenAPI as CommerceOpenAPI } from 'services/generated/commerce-service/core/OpenAPI';
import { getCheckoutRegionFromRegion } from '@whoop/i18n/utils/i18nHelpers';
import { CountryCode } from '@whoop/i18n/types/address';
import SectionHeader from '../SectionHeader/SectionHeader';
import { AmplitudeEvents } from 'whoop-analytics/lib/types';
import { useAnalytics } from '@/hooks/analytics';
import { EXPERIMENT_TRUEDMED_PAYMENTS } from '@/lib/constants/experiments';
import { featureFlagNames } from '@/client/featureFlagClient';

export const getMemberships = async ({
  country,
  language,
}: {
  country: CountryCode;
  language: Language;
}): Promise<Product[]> => {
  try {
    CommerceOpenAPI.BASE = env.NEXT_PUBLIC_COMMERCE_SERVICE_API;

    const membershipCatalog: MembershipCatalog =
      await JoinCatalogResourceV2Service.getMemberships({
        tier: MembershipTier.PEAK,
        country: country,
        language: language,
      });
    return membershipCatalog.memberships;
  } catch (err) {
    return [];
  }
};

interface FAQProps {
  isTrialAvailable?: boolean;
  tier?: MembershipTier;
}

function FAQ({
  isTrialAvailable = false,
  tier = MembershipTier.ONE,
}: FAQProps) {
  const currentPage = useGetCurrentPageMaverick();
  const isUpgrade = usePathname().includes('/upgrade');
  const { t } = useTranslation('faqV2');
  const { trackAmplitudeEvent } = useAnalytics();

  const {
    returnShippingCost,
    language,
    region,
    currency,
    country,
    setPeakAnnualOriginalPrice,
    peakAnnualOriginalPrice,
    isRafGiftFlow,
    shouldShowTruemedAnswer,
  } = useSiteStore(
    useShallow((state) => ({
      currency: state.currency,
      returnShippingCost: state.returnShippingCost,
      region: state.region,
      language: state.language,
      country: state.country,
      setPeakAnnualOriginalPrice: state.setPeakAnnualOriginalPrice,
      peakAnnualOriginalPrice: state.peakAnnualOriginalPrice,
      isRafGiftFlow: state.isRafGiftFlow,
      shouldShowTruemedAnswer:
        state.experimentVariants[EXPERIMENT_TRUEDMED_PAYMENTS] ===
          'experiment' &&
        state.featureFlags[featureFlagNames.TRUEDMED_PAYMENTS],
    })),
  );

  const getPeakMemberships = async () => {
    const memberships = await getMemberships({
      country,
      language,
    });
    const peakAnnualMem = getMembershipByAttributes({
      memberships,
      trialDays: 365,
    });

    if (!peakAnnualMem) return;

    const peakPrice = getPriceValues(peakAnnualMem?.variants[0]);

    setPeakAnnualOriginalPrice(peakPrice.originalPrice ?? 0);
  };

  useEffect(() => {
    if (!peakAnnualOriginalPrice && !isUpgrade) {
      getPeakMemberships();
    }
  }, []);

  const faqs: FrequentlyAskedQuestion[] = useMemo(() => {
    return buildFaqs({
      isUpgrade,
      page: currentPage,
      region,
      isTrialAvailable,
      tier,
      isRafGiftFlow,
      shouldShowTruemedAnswer,
    });
  }, [isRafGiftFlow, shouldShowTruemedAnswer]);

  const formattedPeakAnnualOriginalPrice = useMemo(() => {
    if (!peakAnnualOriginalPrice) return;

    //Should be original price, not display price (to avoid showing the discounted price as the renewal price)
    return formatDisplayPrice(peakAnnualOriginalPrice, currency);
  }, [peakAnnualOriginalPrice]);

  const handleOpenFAQ = (faqKey: string) => {
    trackAmplitudeEvent(AmplitudeEvents.OpenedFAQ, {
      question: faqKey,
    });
  };

  return (
    <div className='flex w-full flex-col items-center bg-gray-50 px-4 py-14'>
      <div className='flex max-w-screen-lg flex-col px-4 md:w-3/4'>
        <SectionHeader className='pb-6'>{t('faqs')}</SectionHeader>
        <Accordion className='w-full' type='multiple'>
          {faqs.map((faq) => {
            const { question, answer, faqKey } = faq;
            return (
              <div key={`membership-faq-${question}`}>
                <Separator />
                <AccordionItem value={`membership-faq-${question}`}>
                  <CustomAccordionTrigger
                    className='group py-3.5 text-left text-sm font-bold uppercase leading-6 tracking-widest'
                    icon={
                      <div className='transition-transform group-data-[state=open]:rotate-180'>
                        <CaretDown fill='black' width={14} />
                      </div>
                    }
                    onOpenItem={() => handleOpenFAQ(faqKey)}
                  >
                    {t(question)}
                  </CustomAccordionTrigger>
                  <AccordionContent className='pb-4 font-normal'>
                    {
                      <Trans
                        components={{
                          terms: (
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            <a
                              className='text-blue-strain underline'
                              href={TERMS_OF_USE_URL}
                              key='tos-link'
                              rel='noopener'
                              target='_blank'
                            />
                          ),
                          returns: (
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            <a
                              className='text-blue-strain underline'
                              href={`https://support.whoop.com/s/article/Return-Policy?language=${language}_${region}`}
                              key='returns-link'
                              rel='noopener'
                              target='_blank'
                            />
                          ),
                          order: (
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            <a
                              className='text-blue-strain underline'
                              href={env.NEXT_PUBLIC_ORDER_STATUS_URL}
                              key='order-link'
                              rel='noopener'
                              target='_blank'
                            />
                          ),
                          app: (
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            <a
                              className='text-blue-strain underline'
                              href={env.NEXT_PUBLIC_APP_URL}
                              key='app-link'
                              rel='noopener'
                              target='_blank'
                            />
                          ),
                          extensions: (
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            <a
                              className='text-blue-strain underline'
                              href={`${APP_WHOOP_GIFT_EXTENSIONS_URL}?region=${getCheckoutRegionFromRegion(
                                region,
                              )}`}
                              key='app-link'
                              rel='noopener'
                              target='_blank'
                            />
                          ),
                          approvals: (
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            <a
                              className='text-blue-strain underline'
                              href={`${env.NEXT_PUBLIC_WHOOP_URL}/feature-availability/`}
                              key='approvals-link'
                              rel='noopener'
                              target='_blank'
                            />
                          ),
                          // eslint-disable-next-line id-length
                          ul: <ul className='list-disc pl-6' key='list' />,
                          // eslint-disable-next-line id-length
                          li: <li key='list-item' />,
                        }}
                        i18nKey={t(answer)}
                        values={{
                          annualPeakPrice: formattedPeakAnnualOriginalPrice, //undefined for upgrade flow since no FAQs use it (no need to make call to get memberships)
                          returnShippingPrice: formatDisplayPrice(
                            // This is the US return shipping cost. The dynamic value will always be available.
                            returnShippingCost ?? 899,
                            currency,
                          ),
                        }}
                      />
                    }
                  </AccordionContent>
                </AccordionItem>
              </div>
            );
          })}
        </Accordion>
        <Separator />
      </div>
    </div>
  );
}

export default FAQ;
